import { useState } from 'react'
import { useT } from '@transifex/react'
import { PaymentMethodFormRef } from '@vend/business-components/dist/components/LspayCnp/index'

type SDKEvent = {
  status: string
  code: string
}

type UsePaymentFormSubmissionProps = {
  onVerifyingPaymentMethodStateChange: (isSubmitting: boolean) => void
  onComplete: () => void
  onError: (message: string) => void
}

export const usePaymentFormSubmission = ({
  onVerifyingPaymentMethodStateChange,
  onComplete,
  onError,
}: UsePaymentFormSubmissionProps) => {
  const t = useT()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [bannerErrorMessage, setBannerErrorMessage] = useState<string>()

  const getDeclinedMessage = (code: SDKEvent['code']): string => {
    switch (code) {
      case 'PaymentMethodNotSupported':
        return t('The Payment method is not supported. Please try again.')
      case 'CardValidation':
        return t(
          'Payment method was declined. Please review the card details and try again.'
        )
      case 'Generic':
      default:
        return t('Payment method was declined. Please try again.')
    }
  }

  const handleNextStep = (
    event: SDKEvent,
    {
      onComplete,
      onError,
      onMessage,
    }: {
      onError: (message: string) => void
      onComplete: () => void
      onMessage: (message: string) => void
    }
  ): void => {
    if (event.status === 'Succeeded' || event.status === 'Pending') {
      onComplete()
      return
    }
    if (event.status === 'Declined') {
      onMessage(getDeclinedMessage(event.code))
      return
    }
    if (event.status === 'Error') {
      if (event.code === 'FormValidation') {
        onMessage(
          t(
            'Please complete all required fields and ensure your card details are correct.'
          )
        )
        return
      }
      onError(t('An unexpected error occurred processing the payment.'))
      return
    }
  }

  const handleProcess = async (
    paymentFormRef: React.RefObject<PaymentMethodFormRef>
  ) => {
    const paymentForm = paymentFormRef.current
    if (!paymentForm) {
      onError(
        t(
          'An unexpected error occurred initializing the payment form. Please try again.'
        )
      )
      return
    }

    try {
      setBannerErrorMessage(undefined)
      setIsSubmitting(true)
      onVerifyingPaymentMethodStateChange(true)
      const result = await paymentForm.submit()

      handleNextStep(result, {
        onComplete,
        onError,
        onMessage: setBannerErrorMessage,
      })
    } catch (error) {
      onError(t('An unexpected error occurred processing the payment.'))
    } finally {
      setIsSubmitting(false)
      onVerifyingPaymentMethodStateChange(false)
    }
  }

  return {
    isSubmitting,
    bannerErrorMessage,
    handleProcess,
  }
}
